import React, { FC, useEffect } from 'react';
import { Card, Table, Tag, Space, Typography, Row, Col, Statistic, Button, Tooltip } from 'antd';
import { UserOutlined, CheckCircleOutlined, ClockCircleOutlined, ReloadOutlined, EyeOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';

import { AppDispatch, CombinedStates } from '../../core/types';
import { getSurveySubmissions } from '../../core/survey/surveyApi';
import { setSingleSurveySubmission, clearSingleSurveySubmission } from '../../core/survey/surveySlice';
import { SurveySubmission } from '../../core/survey/types';
import SurveyAnswerDetail from '../SurveyAnswerDetail';

import './style.scss';

const { Title, Text } = Typography;

const SurveyAnswers: FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { singleSurvey, surveySubmissions, singleSurveySubmission, isFetching } = useSelector((state: CombinedStates) => state.survey);

    useEffect(() => {
        if (singleSurvey?.id) {
            dispatch(getSurveySubmissions({ surveyId: singleSurvey.id }));
        }
    }, [singleSurvey?.id, dispatch]);

    const handleRefresh = () => {
        if (singleSurvey?.id) {
            dispatch(getSurveySubmissions({ surveyId: singleSurvey.id }));
        }
    };

    // Transform API data to table format
    const transformSubmissionsToTableData = (submissions: SurveySubmission[]) => submissions.map((submission, index) => ({
        id: submission.id,
        respondentName: `Atsakovas ${index + 1}`,
        email: submission.unique_hash,
        submittedAt: submission.submitted_at,
        status: 'completed' as const,
        answersCount: submission.answers.length,
        totalQuestions: submission.answers.length, // Assuming all questions are answered
        ipAddress: submission.ip_address,
        userAgent: submission.user_agent,
    }));

    const tableData = transformSubmissionsToTableData(surveySubmissions || []);

    const columns = [
        {
            title: 'Atsakymo ID',
            dataIndex: 'id',
            key: 'id',
            width: 100,
        },
        {
            title: 'Atsakovas',
            dataIndex: 'respondentName',
            key: 'respondentName',
            render: (text: string, record: { id: number; email: string }) => (
                <div>
                    <div style={{ fontWeight: 500 }}>{text}</div>
                    <div style={{ fontSize: '12px', color: '#666' }}>{record.email}</div>
                </div>
            ),
        },
        {
            title: 'Pateikimo data',
            dataIndex: 'submittedAt',
            key: 'submittedAt',
            render: (text: string) => (
                <Text type="secondary">{text}</Text>
            ),
        },
        {
            title: 'Statusas',
            dataIndex: 'status',
            key: 'status',
            render: (status: string) => (
                <Tag
                    color={status === 'completed' ? 'green' : 'orange'}
                    icon={status === 'completed' ? <CheckCircleOutlined /> : <ClockCircleOutlined />}>
                    {status === 'completed' ? 'Užbaigta' : 'Vykdoma'}
                </Tag>
            ),
        },
        {
            title: 'Atsakymai',
            key: 'answers',
            render: (record: { answersCount: number; totalQuestions: number }) => (
                <Text>
                    {record.answersCount} / {record.totalQuestions}
                </Text>
            ),
        },
        {
            title: 'Veiksmai',
            key: 'actions',
            render: (record: { id: number }) => (
                <Space>
                    <Button
                        icon={<EyeOutlined />}
                        onClick={() => {
                            dispatch(setSingleSurveySubmission(record.id));
                        }}>
                        Peržiūrėti
                    </Button>
                </Space>
            ),
        },
    ];

    const completedAnswers = tableData.filter(answer => answer.status === 'completed');
    const totalAnswers = tableData?.length || 0;

    if (singleSurveySubmission) {
        return (
            <SurveyAnswerDetail
                submission={singleSurveySubmission}
                onBack={() => dispatch(clearSingleSurveySubmission())} />
        );
    }

    return (
        <div className="survey-answers">
            <Card>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <Title level={4} style={{ margin: 0 }}>Apklausos atsakymai</Title>
                    <Tooltip title="Atnaujinti atsakymus">
                        <Button
                            type="text"
                            icon={<ReloadOutlined />}
                            onClick={handleRefresh}
                            loading={isFetching}
                            style={{ fontSize: '16px' }} />
                    </Tooltip>
                </div>

                {/* Statistics Row */}
                <Row gutter={16} style={{ marginBottom: 24 }}>
                    <Col span={6}>
                        <Statistic
                            title="Iš viso atsakymų"
                            value={totalAnswers}
                            prefix={<UserOutlined />} />
                    </Col>
                </Row>

                {/* Answers Table */}
                <Table
                    columns={columns}
                    dataSource={tableData}
                    rowKey="id"
                    loading={isFetching}
                    pagination={{
                        pageSize: 10,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) => `${range[0]}-${range[1]} iš ${total} atsakymų`,
                    }}
                    scroll={{ x: 800 }} />
            </Card>
        </div>
    );
};

export default SurveyAnswers;
